import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, catchError, distinctUntilChanged, map, Observable, tap, throwError } from "rxjs";
import { Router } from "@angular/router";
import { environment } from "../../../environments/environment";
import { APIRequestResources } from "../enums";
import { APIRequest } from "../classes";
import { AuthResponse } from "../types";

@Injectable({
    providedIn: 'root'
})
export class AuthService extends APIRequest {
    private readonly API_URL = `${environment.API_BASE}/auth/login`;
    private readonly PORTAL_URL = `${environment.PORTAL}`;
    public readonly APP_NAMESPACE = 'Product Library';
    private readonly authState$ = new BehaviorSubject<boolean>(this.hasValidSession());

    private readonly routePriorityOrder = [
        'task',
        'product-library',
        'production-data',
        'prepress-jobentry',
        'product-development',
        'prepress-dashboard',
    ];

    readonly isAuthenticated$ = this.authState$.asObservable().pipe(
        distinctUntilChanged()
    );

    constructor(protected override http: HttpClient, protected router: Router) {
        super(http, APIRequestResources.AuthService);
    }

    login(authKey: string): Observable<boolean> {
        const headers = new HttpHeaders().set('auth-key', authKey);

        return this.http.post<{ data: AuthResponse }>(this.API_URL, {}, { headers }).pipe(
            map(response => response.data),
            tap(authData => this.storeSession(authData)),
            map(() => true),
            catchError(error => {
                console.error('Authentication error:', error);
                this.clearSession();
                return throwError(() => new Error('Authentication failed'));
            })
        );
    }

    logout(): void {
        this.clearSession();
        window.location.href = this.PORTAL_URL;
    }

    private storeSession(authData: AuthResponse): void {
        if (!authData?.accessToken || !authData?.refreshToken) {
            throw new Error('Invalid authentication response');
        }

        sessionStorage.setItem(`${this.APP_NAMESPACE}_access_token`, authData.accessToken);
        sessionStorage.setItem(`${this.APP_NAMESPACE}_user_info`, JSON.stringify({
            userName: authData.username,
            empNo: authData.employeeNo,
            permissions: authData.userPermissions
        }));
         console.log(JSON.stringify(authData));

        this.authState$.next(true);
    }

    getsession(): string | null {
        const accessToken = sessionStorage.getItem(`${this.APP_NAMESPACE}_access_token`);
        return accessToken;
    }

    public getEmpNo(): string | undefined {
        const userInfo = sessionStorage.getItem(`${this.APP_NAMESPACE}_user_info`);
        if (userInfo) {
            try {
                const parsedUserInfo = JSON.parse(userInfo);
                return parsedUserInfo.empNo || undefined;
            } catch (error) {
                console.error('Error parsing user info:', error);
                return undefined;
            }
        }
        return undefined;
    }

    public getUsername(): string | undefined {
        const userInfo = sessionStorage.getItem(`${this.APP_NAMESPACE}_user_info`);
        if (userInfo) {
            try {
                const parsedUserInfo = JSON.parse(userInfo);
                return parsedUserInfo.userName || undefined;
            } catch (error) {
                console.error('Error parsing user info:', error);
                return undefined;
            }
        }
        return undefined;
    }

    private clearSession(): void {
        sessionStorage.removeItem(`${this.APP_NAMESPACE}_access_token`);
        sessionStorage.removeItem(`${this.APP_NAMESPACE}_user_info`);
        this.authState$.next(false);
    }

    private hasValidSession(): boolean {
        return !!sessionStorage.getItem(`${this.APP_NAMESPACE}_access_token`);
    }

    private getAllowedRoutes(): string[] {
        const userInfoStr = sessionStorage.getItem(`${this.APP_NAMESPACE}_user_info`);
        if (!userInfoStr) return [];

        try {
            const userInfo = JSON.parse(userInfoStr);
            const permissions: { resourcePath: string; ability: string }[] = userInfo?.permissions ?? [];

            return permissions
                .filter(p => p.ability === 'can')
                .map(p => p.resourcePath);
        } catch {
            return [];
        }
    }

    public homePage(): void {
        this.router.navigate(['/home']);
    }
}